import { AccessDenied } from '@/components/ui/access-denied';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CustomLoader } from '@/components/ui/loader';
import { Separator } from '@/components/ui/separator';
import { useAccessControl } from '@/hooks/useAccessControl';
import { useContestGroup } from '@/hooks/useContestGroup';
import { useCreateContestTemplate } from '@/hooks/useContestTemplate';
import { useGetContestFixtureList } from '@/hooks/useFixture';
import {
  CreateContestFormValues,
  createContestSchema,
} from '@/modules/fantasy/create-contest/schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Outlet,
  createFileRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

const searchSchema = z.object({
  id: z.coerce.string().optional(),
});

export const Route = createFileRoute('/dashboard/fantasy/create-contest')({
  validateSearch: searchSchema,
  component: CreateContestLayout,
});

function CreateContestLayout() {
  const { canAccessContest } = useAccessControl();
  const [showDialog, setShowDialog] = useState(false);
  const navigate = useNavigate();
  const { id: fixtureId } = useSearch({
    from: '/dashboard/fantasy/create-contest',
  });

  const {
    data: contestsFixtureDetails,
    isLoading: contestsFixtureDetailsIsLoading,
  } = useGetContestFixtureList({ fixtureId: Number(fixtureId) });

  const { isLoading, data: contestGroupData } = useContestGroup();
  const contestGroupsMap = useMemo(() => {
    if (!isLoading && contestGroupData) {
      return contestGroupData.data.groups.reduce(
        (
          acc: Record<string, string>,
          group: { groupId: number; name: string }
        ) => {
          acc[group.groupId.toString()] = group.name;
          return acc;
        },
        {}
      );
    } else {
      return {};
    }
  }, [contestGroupData, isLoading]);

  const methods = useForm<CreateContestFormValues>({
    resolver: zodResolver(createContestSchema),
    defaultValues: {
      sportType: 'CRICKET',
      minParticipants: 2,
      autoPublish: true,
      rewardMatrix: [
        {
          amount: 0,
          prizeType: 'GT',
          rankStart: 1,
          rankEnd: 1,
        },
      ],
    },
  });

  useEffect(() => {
    if (fixtureId && fixtureId !== '') {
      methods.setValue('fixtureId', fixtureId);
      methods.setValue('autoPublish', true);
    }
  }, [fixtureId, methods]);

  const { mutateAsync, isPending } = useCreateContestTemplate({
    excludeSportType: true,
  });

  if (!canAccessContest) {
    return <AccessDenied />;
  }

  const totalDistributedPrizePool =
    methods.watch('rewardMatrix').reduce((sum, item) => {
      return sum + item.amount * (item.rankEnd - item.rankStart + 1);
    }, 0) ?? 0;

  const onSubmit = async (values: CreateContestFormValues) => {
    try {
      if (!isPending) {
        await mutateAsync(values);
        toast.success('Contest added successfully', {
          className: 'bg-black text-white border-0',
          position: 'bottom-right',
          duration: 3000,
        });
      }
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong', {
        className: 'bg-black text-white border-0',
        position: 'bottom-right',
        duration: 3000,
      });
    }
  };

  const handleProceed = async () => {
    setShowDialog(true);
  };

  if (isLoading || contestsFixtureDetailsIsLoading) {
    return <CustomLoader />;
  }

  return (
    <FormProvider {...methods}>
      <div className='flex flex-1 flex-row justify-center items-center w-full mt-6 md:mt-10 px-4'>
        <h1 className='text-lg md:text-xl lg:text-2xl font-base text-center'>
          {contestsFixtureDetails?.fixtureTitle}
        </h1>
      </div>
      <div className='flex w-full justify-center items-center mt-2 md:mt-4 px-4'>
        <div className='flex flex-col md:flex-row w-full md:w-4/5 lg:w-3/5 px-4 md:px-8 self-center items-center gap-4'>
          <img
            src={contestsFixtureDetails?.teams.home.teamLogo ?? ''}
            alt='Home Team Logo'
            width={100}
            height={100}
            className='rounded-full overflow-hidden w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 object-cover'
          />
          <div className='flex flex-col w-full items-center'>
            <div className='flex flex-col md:flex-row items-center'>
              <h1 className='text-base md:text-lg lg:text-xl font-base'>
                ({contestsFixtureDetails?.teams.home.teamAbbr})
              </h1>
              <h1 className='text-base md:text-lg lg:text-xl font-base mx-2'>
                {' vs '}
              </h1>
              <h1 className='text-base md:text-lg lg:text-xl font-base'>
                ({contestsFixtureDetails?.teams.away.teamAbbr})
              </h1>
            </div>
            <h1 className='text-sm md:text-base lg:text-lg font-base mt-2'>
              {contestsFixtureDetails?.displayDate ?? 'Display date'}
            </h1>
            <p className='text-xs md:text-sm text-muted-foreground text-center'>
              Manage and monitor your fantasy contests
            </p>
          </div>
          <img
            src={contestsFixtureDetails?.teams.away.teamLogo ?? ''}
            alt='Away Team Logo'
            width={100}
            height={100}
            className='rounded-full overflow-hidden w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 object-cover'
          />
        </div>
      </div>
      <div className='p-4 md:p-6 lg:px-20 flex w-full h-full items-center justify-center my-12 md:my-24'>
        <Card className='flex flex-1 w-full'>
          <CardContent className='p-4 md:p-6'>
            <Outlet />
            <Separator className='my-6' />
            <div className='flex flex-col sm:flex-row justify-end gap-2 sm:gap-4'>
              <Button
                type='button'
                variant='destructive'
                className='w-full sm:w-24'
                onClick={() => navigate({ to: '..' })}
              >
                Cancel
              </Button>
              <Button
                type='button'
                className='w-full sm:w-24'
                onClick={handleProceed}
              >
                Proceed
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              <Card>
                <CardContent className='space-y-2 h-[50vh] overflow-scroll'>
                  <h2 className='text-lg font-base'>Contest Details</h2>

                  <div className='w-full flex flex-row justify-between'>
                    <strong>Name:</strong> {methods.watch('name') || ''}
                  </div>
                  <div className='w-full flex flex-row justify-between'>
                    <strong>Category:</strong>
                    <Badge>
                      {contestGroupsMap[methods.watch('contestGroupId')] || ''}
                    </Badge>
                  </div>
                  <div className='w-full flex flex-row justify-between'>
                    <strong>Type:</strong> {methods.watch('contestType') || ''}
                  </div>
                  <div className='w-full flex flex-row justify-between'>
                    <strong>Entry Fee:</strong>
                    {methods.watch('entryFee') ?? ''}
                  </div>

                  <div className='w-full flex flex-row justify-between'>
                    <strong>Min Players:</strong>{' '}
                    {methods.watch('minParticipants') ?? ''}
                  </div>
                  <div className='w-full flex flex-row justify-between'>
                    <strong>Max Players:</strong>{' '}
                    {methods.watch('maxParticipants') ?? ''}
                  </div>
                  <div className='w-full flex flex-row justify-between'>
                    <strong>Max Teams/User:</strong>{' '}
                    {methods.watch('maxEntries') ?? ''}
                  </div>

                  <div className='w-full flex flex-row justify-between'>
                    <strong>Mutli Join:</strong>{' '}
                    {methods.watch('isMultijoin') ? '✅' : '❌'}
                  </div>
                  <div className='w-full flex flex-row justify-between'>
                    <strong>Auto Recurring:</strong>{' '}
                    {methods.watch('autoRecurring') ? '✅' : '❌'}
                  </div>
                  <div className='w-full flex flex-row justify-between'>
                    <strong>Priority :</strong>{' '}
                    <Badge>{methods.watch('priority')}</Badge>
                  </div>
                  <div className='w-full flex flex-row justify-between'>
                    <strong>Prize Pool :</strong>{' '}
                    <Badge>{totalDistributedPrizePool}</Badge>
                  </div>
                  <Separator />
                  <strong>Prize Distribution:</strong>
                  <Card className='mt-5'>
                    <CardContent className='space-y-2'>
                      <div className='flex font-base text-sm text-muted-foreground justify-between px-2'>
                        <span>From</span>
                        <span>To</span>
                        <span>Amt</span>
                        <span>Type</span>
                        <span>Total</span>
                      </div>
                      <Separator />

                      {methods.watch('rewardMatrix')?.map((item, index) => (
                        <div key={index}>
                          <div className='flex flex-row justify-between items-center gap-4 px-2'>
                            <Badge variant='outline'> {item.rankStart} </Badge>
                            <Badge variant='outline'> {item.rankEnd} </Badge>
                            <span className='text-sm'>{item.amount}</span>
                            <span className='text-sm'>{item.prizeType}</span>
                            <span className='font-medium text-sm'>
                              {item.amount *
                                (item.rankEnd - item.rankStart + 1)}
                            </span>
                          </div>
                          {index < methods.watch('rewardMatrix').length - 1 && (
                            <Separator className='my-2' />
                          )}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </CardContent>
              </Card>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={e => {
                e.preventDefault();
                onSubmit(methods.getValues());
              }}
            >
              Add Contest to Fixture
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FormProvider>
  );
}
